import React, { useState } from 'react';
import { Box, Typography, Chip, Paper, Divider, Button, Tooltip } from '@mui/material';
import {
  CheckCircle as PassIcon,
  Cancel as FailIcon,
  RemoveCircleOutline as AbsentIcon,
  EmojiEvents as TrophyIcon,
  School as SchoolIcon,
  ReportProblem as ReportProblemIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import GradeReclamationDialog from './GradeReclamationDialog';

/* ── Semester accent colours ── */
const SEM_COLORS = {
  S1: { main: '#2563eb', light: '#eff6ff', border: '#bfdbfe' },
  S2: { main: '#d97706', light: '#fffbeb', border: '#fde68a' },
  S3: { main: '#16a34a', light: '#f0fdf4', border: '#bbf7d0' },
  S4: { main: '#7c3aed', light: '#f5f3ff', border: '#ddd6fe' },
  S5: { main: '#dc2626', light: '#fff1f2', border: '#fecdd3' },
  S6: { main: '#0891b2', light: '#ecfeff', border: '#a5f3fc' },
  S0: { main: '#64748b', light: '#f8fafc', border: '#e2e8f0' },
};

/* ── Result code metadata ── */
const RESULT_META = {
  V:   { color: '#15803d', bg: '#dcfce7', labelKey: 'gt_V',   icon: 'pass' },
  NV:  { color: '#dc2626', bg: '#fee2e2', labelKey: 'gt_NV',  icon: 'fail' },
  VAR: { color: '#b45309', bg: '#fef3c7', labelKey: 'gt_VAR', icon: 'pass' },
  ACR: { color: '#1d4ed8', bg: '#dbeafe', labelKey: 'gt_ACR', icon: 'pass' },
  VAD: { color: '#15803d', bg: '#dcfce7', labelKey: 'gt_V',   icon: 'pass' },
  AB:  { color: '#6b7280', bg: '#f3f4f6', labelKey: 'gt_AB',  icon: 'absent' },
};

// Result codes that count as validated regardless of numeric grade
const PASSING_CODES = new Set(['V', 'VAR', 'ACR', 'AC', 'VE', 'VAD']);

// Semesters covered by the exam-planning based reclamation workflow
const RECLAIMABLE_SEMESTERS = new Set([2, 4, 6]);

// Reclamation status -> chip styling
const RECLAMATION_STATUS_META = {
  pending:     { color: '#b45309', bg: '#fef3c7', labelKey: 'recl_status_pending' },
  in_progress: { color: '#1d4ed8', bg: '#dbeafe', labelKey: 'recl_status_in_progress' },
  resolved:    { color: '#15803d', bg: '#dcfce7', labelKey: 'recl_status_resolved' },
  rejected:    { color: '#dc2626', bg: '#fee2e2', labelKey: 'recl_status_rejected' },
};

const semStyle = (s) => SEM_COLORS[s] || SEM_COLORS.S0;

/* ── Grade badge ── */
const GradeBadge = ({ note, cod_tre }) => {
  if (note === null || note === undefined)
    return <Box sx={{ width: 60, textAlign: 'center', color: '#94a3b8', fontSize: '1rem', fontWeight: 700 }}>—</Box>;
  const val = parseFloat(note);
  const pass = PASSING_CODES.has(cod_tre) || val >= 10;
  return (
    <Box sx={{
      minWidth: 60, height: 40, borderRadius: 2, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      bgcolor: pass ? '#dcfce7' : '#fee2e2',
      border: `1.5px solid ${pass ? '#86efac' : '#fca5a5'}`,
    }}>
      <Typography sx={{ fontSize: '1rem', fontWeight: 800, lineHeight: 1, color: pass ? '#15803d' : '#dc2626' }}>
        {val.toFixed(2)}
      </Typography>
      <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: pass ? '#16a34a' : '#dc2626', opacity: 0.8 }}>
        /20
      </Typography>
    </Box>
  );
};

/* ── Result chip ── */
const ResultChip = ({ code, t }) => {
  if (!code) return null;
  const meta = RESULT_META[code];
  if (!meta) return <Chip label={code} size="small" sx={{ height: 22, fontWeight: 700, borderRadius: 1.5 }} />;
  return (
    <Chip
      label={t(meta.labelKey)}
      size="small"
      sx={{ height: 22, borderRadius: 1.5, fontWeight: 700, fontSize: '0.7rem', bgcolor: meta.bg, color: meta.color }}
    />
  );
};

/* ── Single module row ── */
const ModuleRow = ({
  grade, accent, t, hasArabicNames, index, isRattrapageSession,
  studyYear, activeSession, reclaimableModules, myReclamations, onOpenReclamation,
  showReclColumn, reclamationsEnabled = true, hideResult = false,
}) => {
  const pass   = PASSING_CODES.has(grade.cod_tre) || (grade.not_elp !== null && parseFloat(grade.not_elp) >= 10);
  const absent = grade.cod_tre === 'AB';

  const baseCode = (grade.cod_elp || '').replace(/(CF|CC)$/, '');
  const isZeroOrAbsent = (grade.not_elp !== null && parseFloat(grade.not_elp) === 0)
    || (grade.not_elp === null && grade.eta_not_elp === 'N');
  const eligible = isZeroOrAbsent
    && RECLAIMABLE_SEMESTERS.has(grade.semester_number)
    && !!activeSession
    && reclaimableModules.has(baseCode);

  const existingReclamation = eligible
    ? myReclamations.find(r => r.module_code === baseCode && r.exam_session_id === activeSession.id)
    : null;

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 2,
      px: 2, py: 1.25,
      bgcolor: index % 2 === 0 ? 'white' : '#fafafa',
      borderBottom: '1px solid #f1f5f9',
      '&:last-child': { borderBottom: 'none' },
      transition: 'background 0.12s',
      '&:hover': { bgcolor: accent.light },
    }}>
      {/* Status icon */}
      <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
        {absent
          ? <AbsentIcon sx={{ fontSize: 18, color: '#9ca3af' }} />
          : pass
            ? <PassIcon sx={{ fontSize: 18, color: '#16a34a' }} />
            : <FailIcon sx={{ fontSize: 18, color: '#dc2626' }} />}
      </Box>

      {/* Module name */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" fontWeight={600} sx={{ color: '#1e293b', lineHeight: 1.3 }}>
            {(hasArabicNames && grade.lib_elp_arb) ? grade.lib_elp_arb : grade.lib_elp || '—'}
          </Typography>
          {isRattrapageSession && (
            <Chip label="استدراك" size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#fef3c7', color: '#b45309', borderRadius: 1 }} />
          )}
        </Box>
        {hasArabicNames && grade.lib_elp_arb && grade.lib_elp && (
          <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ mt: 0.2 }}>
            {grade.lib_elp}
          </Typography>
        )}
      </Box>

      {/* Code */}
      <Typography variant="caption" sx={{
        fontFamily: 'monospace', bgcolor: '#f1f5f9', px: 1, py: 0.25,
        borderRadius: 1, color: '#64748b', fontWeight: 600, flexShrink: 0,
        display: { xs: 'none', sm: 'block' },
      }}>
        {grade.cod_elp}
      </Typography>

      {/* Grade */}
      <GradeBadge note={grade.not_elp} cod_tre={grade.cod_tre} />

      {/* Result */}
      {!hideResult && (
        <Box sx={{ flexShrink: 0, minWidth: 80, display: { xs: 'none', sm: 'flex' }, justifyContent: 'flex-end' }}>
          <ResultChip code={grade.cod_tre} t={t} />
        </Box>
      )}

      {/* Reclamation — box is always rendered when block has eligible rows (keeps column widths uniform) */}
      {showReclColumn && (
        <Box sx={{ flexShrink: 0, minWidth: 110, display: 'flex', justifyContent: 'flex-end' }}>
          {eligible && (existingReclamation ? (
            <Tooltip
              title={existingReclamation.admin_response || ''}
              arrow
              disableHoverListener={!existingReclamation.admin_response}
            >
              <Chip
                label={t(RECLAMATION_STATUS_META[existingReclamation.status]?.labelKey || existingReclamation.status)}
                size="small"
                sx={{
                  height: 22, borderRadius: 1.5, fontWeight: 700, fontSize: '0.7rem',
                  bgcolor: RECLAMATION_STATUS_META[existingReclamation.status]?.bg,
                  color: RECLAMATION_STATUS_META[existingReclamation.status]?.color,
                }}
              />
            </Tooltip>
          ) : (reclamationsEnabled && (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<ReportProblemIcon sx={{ fontSize: 16 }} />}
              onClick={() => onOpenReclamation({ grade, baseCode, studyYear })}
              sx={{ height: 26, fontSize: '0.7rem', borderRadius: 1.5, textTransform: 'none', px: 1, whiteSpace: 'nowrap' }}
            >
              {t('recl_button')}
            </Button>
          )))}
        </Box>
      )}
    </Box>
  );
};

/* ── Semester block ── */
const SemesterBlock = ({
  semKey, grades, sessionNum, t, hasArabicNames,
  studyYear, activeSession, reclaimableModules, myReclamations, onOpenReclamation,
  reclamationsEnabled = true, hideResult = false,
}) => {
  const style = semStyle(semKey);
  const semNum = semKey.replace('S', '');
  const isRattrapage = sessionNum === '2';

  // Determine if any grade in this block is eligible so we can reserve column space for ALL rows
  const hasAnyEligible = !!activeSession && grades.some(g => {
    const base = (g.cod_elp || '').replace(/(CF|CC)$/, '');
    const zeroOrAbsent = (g.not_elp !== null && parseFloat(g.not_elp) === 0)
      || (g.not_elp === null && g.eta_not_elp === 'N');
    return zeroOrAbsent && RECLAIMABLE_SEMESTERS.has(g.semester_number) && reclaimableModules.has(base);
  });

  const seasonLabel = isRattrapage ? t('catchupSession') : (parseInt(semNum) % 2 === 1 ? t('gt_autumn') : t('gt_spring'));

  return (
    <Paper elevation={0} sx={{ borderRadius: 3, border: `1.5px solid ${style.border}`, overflow: 'hidden', mb: 2.5 }}>

      {/* Header */}
      <Box sx={{ px: 2.5, py: 1.8, bgcolor: style.light, borderBottom: `1.5px solid ${style.border}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {/* Semester badge */}
          <Box sx={{
            width: 44, height: 44, borderRadius: 2, flexShrink: 0,
            bgcolor: style.main, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: 'white', opacity: 0.85, lineHeight: 1 }}>S</Typography>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 900, color: 'white', lineHeight: 1 }}>{semNum}</Typography>
          </Box>

          {/* Title */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={800} sx={{ color: style.main, lineHeight: 1.2 }}>
              {t('gt_semester')} {semNum}
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={500}>
              {seasonLabel}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Column header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 0.75,
        bgcolor: '#f8fafc', borderBottom: '1px solid #f1f5f9',
      }}>
        <Box sx={{ width: 18 }} />
        <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ flex: 1 }}>{t('gt_moduleName')}</Typography>
        <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ display: { xs: 'none', sm: 'block' }, minWidth: 70 }}>{t('gt_code')}</Typography>
        <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ minWidth: 60, textAlign: 'center' }}>{t('gt_grade')}</Typography>
        {!hideResult && (
          <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ display: { xs: 'none', sm: 'block' }, minWidth: 80, textAlign: 'right' }}>{t('gt_result')}</Typography>
        )}
        {hasAnyEligible && <Box sx={{ flexShrink: 0, minWidth: 110 }} />}
      </Box>

      {/* Rows */}
      {grades
        .sort((a, b) => (a.lib_elp || '').localeCompare(b.lib_elp || ''))
        .map((g, i) => (
          <ModuleRow
            key={`${g.cod_elp}-${i}`}
            grade={g}
            accent={style}
            t={t}
            hasArabicNames={hasArabicNames}
            index={i}
            isRattrapageSession={isRattrapage}
            studyYear={studyYear}
            activeSession={activeSession}
            reclaimableModules={reclaimableModules}
            myReclamations={myReclamations}
            onOpenReclamation={onOpenReclamation}
            showReclColumn={hasAnyEligible}
            reclamationsEnabled={reclamationsEnabled}
            hideResult={hideResult}
          />
        ))}
    </Paper>
  );
};

/* ── Year summary card ── */
const YearSummary = ({ sessionData, studyYear, t }) => {
  return (
    <Box sx={{
      background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
      borderRadius: 3, px: 3, py: 2, mb: 2.5, color: 'white',
      display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
    }}>
      <SchoolIcon sx={{ fontSize: 22, opacity: 0.7 }} />
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle1" fontWeight={800} lineHeight={1.2}>
          {t('gt_academicYear')} {parseInt(studyYear) - 1} — {studyYear}
        </Typography>
      </Box>
    </Box>
  );
};

/* ══════════════════════════════════════════
   Main GradeTable component
   Data shape: { [year]: { [session]: { [semCode]: grade[] } } }
══════════════════════════════════════════ */
const GradeTable = ({
  gradesData, hasArabicNames = false,
  activeSession = null, reclaimableModules = new Set(), myReclamations = [],
  onReclamationSubmitted = () => {}, reclamationsEnabled = true, hideResult = false,
}) => {
  const { t } = useTranslation();
  const [reclamationTarget, setReclamationTarget] = useState(null);

  if (!gradesData || Object.keys(gradesData).length === 0) return null;

  const handleReclamationSubmitted = () => {
    setReclamationTarget(null);
    onReclamationSubmitted();
  };

  return (
    <Box>
      {Object.entries(gradesData)
        .sort(([a], [b]) => parseInt(b) - parseInt(a))
        .map(([studyYear, yearData]) => (
          <Box key={studyYear} sx={{ mb: 1 }}>
            <YearSummary sessionData={yearData} studyYear={studyYear} t={t} />

            {Object.entries(yearData)
              .sort(([a], [b]) => parseInt(a) - parseInt(b))
              .flatMap(([sessionNum, sessionData]) =>
                Object.entries(sessionData)
                  .sort(([a], [b]) => parseInt(a.replace('S', '')) - parseInt(b.replace('S', '')))
                  .map(([semKey, grades]) => (
                    <SemesterBlock
                      key={`${sessionNum}-${semKey}`}
                      semKey={semKey}
                      grades={grades}
                      sessionNum={sessionNum}
                      t={t}
                      hasArabicNames={hasArabicNames}
                      studyYear={studyYear}
                      activeSession={activeSession}
                      reclaimableModules={reclaimableModules}
                      myReclamations={myReclamations}
                      onOpenReclamation={setReclamationTarget}
                      reclamationsEnabled={reclamationsEnabled}
                      hideResult={hideResult}
                    />
                  ))
              )}
          </Box>
        ))}

      {/* Legend */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1.5px solid #e8ecef', p: 2, mt: 1 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" mb={1}>
          {t('gt_legendTitle')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {Object.entries(RESULT_META).map(([code, meta]) => (
            <Box key={code} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Chip label={code} size="small" sx={{ height: 20, borderRadius: 1, fontWeight: 700, bgcolor: meta.bg, color: meta.color, fontSize: '0.7rem' }} />
              <Typography variant="caption" color="text.secondary">{t(meta.labelKey)}</Typography>
            </Box>
          ))}
          <Divider orientation="vertical" flexItem />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <PassIcon sx={{ fontSize: 14, color: '#16a34a' }} />
            <Typography variant="caption" color="text.secondary">{t('gt_passedIcon')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <FailIcon sx={{ fontSize: 14, color: '#dc2626' }} />
            <Typography variant="caption" color="text.secondary">{t('gt_failedIcon')}</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Reclamation dialog */}
      <GradeReclamationDialog
        open={!!reclamationTarget}
        target={reclamationTarget}
        activeSession={activeSession}
        onClose={() => setReclamationTarget(null)}
        onSubmitted={handleReclamationSubmitted}
      />
    </Box>
  );
};

export default GradeTable;
